import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useWatch, useFormContext } from 'react-hook-form'
import classnames from 'classnames'

import minBy from 'lodash/minBy'

import {
  DiningOptionBehavior,
  FulfillmentType,
  ShippingRate,
  useCartShippingRatesQuery,
  useSelectShippingRateMutation
} from 'src/apollo/onlineOrdering'
import { reportError } from 'src/lib/js/clientError'
import { useFulfillment } from 'src/public/components/online_ordering/FulfillmentContext'
import InputField from 'src/shared/components/common/form_input/InputField'
import { formatAddress } from 'src/shared/components/common/form_input/LocationInput'
import { t } from 'src/translations'

import { ToggleInput } from 'shared/components/common/forms'
import { LoadingSpinner } from 'shared/components/common/loading_spinner/LoadingSpinner'
import PlacesAutocomplete from 'shared/components/common/location_search/PlacesAutocomplete'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { getEcommShippingFee } from 'public/components/default_template/online_ordering/cart/cartUtils'
import { useValidateCartForShipping } from 'public/components/default_template/online_ordering/cart/useValidateCartForShipping'
import CheckoutSection from 'public/components/default_template/online_ordering/checkout/CheckoutSection'
import ShippingDateSection from './ShippingDateSection'
import {
  AddressType,
  getDeliveryAddress,
  saveDeliveryAddress
} from 'public/components/default_template/online_ordering/checkout/shippingUtils'
import { useCart } from 'public/components/online_ordering/CartContext'
import {
  normalizeGoogleAddress,
  formatDeliveryAddress
} from 'public/components/online_ordering/addressUtils'

import { gPlacesAPIKey } from 'config'

export const ShippingSection = ({
  disableControls = false
}: {
  disableControls?: boolean
} = {}) => {
  const firstName = useWatch({ name: 'yourInfoFirstName' })
  const lastName = useWatch({ name: 'yourInfoLastName' })
  const { selectedLocation, restaurant, ooRestaurant } = useRestaurant()
  const [address, setAddress] = useState<AddressType | undefined>()
  const [estimatedDays, setEstimatedDays] = useState<number | undefined>()
  const [rateSelectionLoading, setRateSelectionLoading] = useState(false)
  const { cartGuid, cart } = useCart()
  const { updateCartFulfillment } = useFulfillment()
  const { register, setValue, trigger } = useFormContext()
  const selectedShippingRate = getEcommShippingFee(cart)?.guid
  const { refetchValidateCart } = useValidateCartForShipping()

  register('address', {
    required: true,
    validate: (addressInput) => {
      return (
        formatDeliveryAddress(address, { full: true, includeZip: false }) ===
        addressInput
      )
    }
  })

  useEffect(() => {
    // trigger address validation every a user selects a new address from the places autocomplete component
    trigger('address')
  }, [address, selectedShippingRate, trigger])

  const onShippingAddressInputChange = useCallback(
    (address: string) => {
      setValue('address', address, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      })
    },
    [setValue]
  )

  const onSetAddress = useCallback(
    async (address: AddressType) => {
      if (selectedLocation.externalId) {
        saveDeliveryAddress(address, selectedLocation.externalId)
      }
      const { name, ...deliveryInfo } = address
      await updateCartFulfillment({
        deliveryInfo,
        fulfillmentType: FulfillmentType.Asap,
        diningOptionBehavior: DiningOptionBehavior.TakeOut
      })
      setAddress(address)
      onShippingAddressInputChange(
        formatDeliveryAddress(address, { full: true, includeZip: false })
      )
      // Revalidate cart for shipping after address change
      refetchValidateCart()
    },
    [
      updateCartFulfillment,
      selectedLocation,
      onShippingAddressInputChange,
      refetchValidateCart
    ]
  )

  useEffect(() => {
    const storedAddress = getDeliveryAddress(selectedLocation.externalId)
    if (storedAddress) {
      onSetAddress(storedAddress)
    }
  }, [selectedLocation, onSetAddress])

  const onPlaceSelected = useCallback(
    async (place: google.maps.places.PlaceResult) => {
      const normalizedAddress = normalizeGoogleAddress(place)
      if (normalizedAddress) {
        const address = {
          name: `${firstName} ${lastName}`,
          ...normalizedAddress
        }

        onSetAddress(address)
      }
    },
    [firstName, lastName, onSetAddress]
  )

  return (
    <CheckoutSection>
      <div
        className={classnames('shippingSection', {
          disabled: disableControls
        })}
        aria-busy={disableControls}
      >
        <span role='heading' aria-level={2} className='shippingSectionHeader'>
          {t('shipping-section.shipping-address')}
        </span>
        <div className='addressInput' data-testid='shipping-address-input'>
          <PlacesAutocomplete
            formInputField
            fieldLabel={t('shipping-section.address-label')}
            inputAriaLabel={t('shipping-section.enter-your-shipping-address')}
            className='addressInputField'
            id='shipping-address-input-autocomplete'
            placeholder={t('shipping-section.enter-your-shipping-address')}
            defaultValue={
              address
                ? formatAddress({
                    address1: address.address1,
                    city: address.city,
                    state: address.state,
                    zipcode: address.zipCode
                  })
                : undefined
            }
            apiKey={gPlacesAPIKey}
            onPlaceSelected={onPlaceSelected}
            onValueChange={onShippingAddressInputChange}
            locationBias={
              selectedLocation?.lat && selectedLocation?.long
                ? { lat: selectedLocation.lat, long: selectedLocation.long }
                : undefined
            }
          />
        </div>
        {address ? (
          <InputField
            label={t('shipping-section.apartment-suite-optional')}
            id='address2'
            type='text'
            defaultValue={address?.address2 || undefined}
            preventFormSubmit
            onBlur={(e) => {
              if (e.target.value && address) {
                onSetAddress({
                  ...address,
                  address2: e.target.value
                })
              }
            }}
          />
        ) : null}
        {address && restaurant.id && ooRestaurant?.guid && cartGuid && (
          <>
            <div>
              <hr className='shippingMethodSectionDivider' />
              <ShippingRateSelector
                siteGuid={restaurant.id}
                restaurantGuid={ooRestaurant.guid}
                cartGuid={cartGuid}
                address={address}
                onRateChange={(rate) => setEstimatedDays(rate?.estimatedDays)}
                onLoadingChange={setRateSelectionLoading}
              />
            </div>
            <div>
              <hr className='shippingDateSectionDivider' />
              <ShippingDateSection
                estimatedDays={estimatedDays}
                rateSelectionLoading={rateSelectionLoading}
              />
            </div>
          </>
        )}
      </div>
    </CheckoutSection>
  )
}

export const ShippingRateSelector = ({
  siteGuid,
  restaurantGuid,
  cartGuid,
  address,
  onRateChange,
  onLoadingChange
}: {
  siteGuid: string
  restaurantGuid: string
  cartGuid: string
  address: AddressType
  onRateChange?: (rate: ShippingRate | undefined) => void
  onLoadingChange?: (loading: boolean) => void
}) => {
  const [setSelectedShippingRate] = useSelectShippingRateMutation()
  const { cart, refetchCart } = useCart()
  const [error, setError] = useState<string>()
  const isInitialMount = useRef(true)

  const {
    data,
    error: shippingRatesError,
    loading,
    refetch
  } = useCartShippingRatesQuery({
    variables: {
      input: {
        siteGuid,
        cartGuid,
        restaurantGuid: restaurantGuid
      }
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  })

  const cartShippingRate: ShippingRate | null | undefined =
    data?.getCartShippingRates.find(
      (rate) => getEcommShippingFee(cart)?.guid === rate?.shippingRateGuid
    )
  const [selectedRate, setSelectedRate] = useState<ShippingRate | undefined>(
    cartShippingRate || undefined
  )

  const selectShippingRate = useCallback(
    async (selectedShippingRate: ShippingRate) => {
      setSelectedRate(selectedShippingRate)
      onLoadingChange?.(true)

      try {
        const { data } = await setSelectedShippingRate({
          variables: {
            input: {
              siteGuid,
              cartGuid,
              restaurantGuid,
              selectedShippingRateGuid: selectedShippingRate.shippingRateGuid
            }
          }
        })
        if (
          data?.setSelectedShippingRate?.__typename ===
          'EcommerceMutationErrorResponse'
        ) {
          setError(
            `Error setting shipping rate: ${data?.setSelectedShippingRate.message}`
          )
        } else {
          setError(undefined)
        }
        await refetchCart()
        // Call onRateChange AFTER the cart has been updated
        onRateChange?.(selectedShippingRate)
      } finally {
        onLoadingChange?.(false)
      }
    },
    [
      setSelectedShippingRate,
      setSelectedRate,
      cartGuid,
      siteGuid,
      restaurantGuid,
      refetchCart,
      onRateChange,
      onLoadingChange
    ]
  )

  useEffect(() => {
    if (loading) {
      return
    }

    let newShippingRate
    const isSelectedRateUnavailable = !data?.getCartShippingRates.find(
      (rate) => rate?.shippingRateGuid === selectedRate?.shippingRateGuid
    )
    const cheapestRate = minBy(
      data?.getCartShippingRates,
      (rate: ShippingRate) => parseFloat(rate?.amount)
    )

    if (!selectedRate?.shippingRateGuid) {
      // by default, select the cheapest rate available
      newShippingRate = cheapestRate
    } else if (isSelectedRateUnavailable) {
      // keep the user's selected rate after they change addresses or shipping rates are updated
      newShippingRate =
        data?.getCartShippingRates.find(
          (rate) => rate?.title === selectedRate?.title
        ) || cheapestRate
    }

    if (newShippingRate) {
      selectShippingRate(newShippingRate)
    }
  }, [data?.getCartShippingRates, loading, selectShippingRate, selectedRate])

  useEffect(() => {
    if (shippingRatesError) {
      setError(t('shipping-section.error-calculating-shipping-rates'))
      reportError('Error calculating shipping rates', shippingRatesError)
    }
  }, [shippingRatesError])

  useEffect(() => {
    // Force a refetch when the address changes. This assumes `updateCartFulfillment` has been called successfully
    setError(undefined)
    refetch()
  }, [address, refetch])

  useEffect(() => {
    // Skip the initial mount to avoid double refetch (address-change effect already refetches on mount)
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Refetch shipping rates when cart contents change (items added/removed or quantities updated)
    // Shipping rates depend on package weight/dimensions which change with cart contents
    if (cart?.order) {
      setError(undefined) // Clear any previous error state before refetching
      refetch()
    }
  }, [
    cart?.order?.numberOfSelections,
    cart?.order?.preDiscountItemsSubtotal,
    refetch
  ])

  return (
    <div className='shippingRatesSection'>
      {loading ? (
        <div className='loader'>
          <LoadingSpinner />
        </div>
      ) : (
        <div className='shippingRates' role='radiogroup'>
          <span role='heading' aria-level={2} className='shippingSectionHeader'>
            {t('shipping-section.shipping-method')}
          </span>
          {data?.getCartShippingRates.map((rate: ShippingRate) => (
            <ToggleInput
              additionalClassNames={
                selectedRate?.shippingRateGuid === rate.shippingRateGuid
                  ? 'selected'
                  : ''
              }
              name={rate.title}
              key={rate.shippingRateGuid}
              id={`shipping-rate-option-${rate.shippingRateGuid}`}
              type='radio'
              checked={selectedRate?.shippingRateGuid === rate.shippingRateGuid}
              onChange={() => selectShippingRate(rate)}
            >
              <div className='shippingRateOption'>
                <div className='rate'>
                  <div>{rate?.title}</div>
                  {rate?.amount && (
                    <div>{`${rate?.currency === 'USD' ? '$' : ''}${parseFloat(
                      rate.amount
                    ).toFixed(2)}`}</div>
                  )}
                </div>
                <div className='estimatedDays'>
                  {rate.description ||
                    t('shipping-section.delivery-estimate-not-available')}
                </div>
              </div>
            </ToggleInput>
          ))}
          {error && <div>{error}</div>}
        </div>
      )}
    </div>
  )
}
